import { useCallback, useEffect, useMemo, useState } from 'react'
import { daysUntil, daysSince } from '../lib/when'
import CollectionRow from '../components/CollectionRow'
import { heldText } from '../lib/cycle'
import { isReachable } from '../lib/chase'
import ActivityDrawer from '../components/ActivityDrawer'
import LedgerView from '../components/LedgerView'
import ClientLedgers from '../components/ClientLedgers'
import Modal from '../components/Modal'
import Icon from '../components/Icon'
import Select from '../components/Select'
import { SkeletonLine, SkeletonRow } from '../components/Skeleton'
import * as api from '../lib/api'
import { useCanAct } from '../lib/access'
import { fetchAllLedger } from '../lib/ledgerApi'
import {
  CATEGORY_OPTIONS,
  CHASE_OPTIONS,
  COLLECTION_OPTIONS,
  PROBLEM_OPTIONS,
  RECONCILIATION_OPTIONS,
  DEFAULT_QUERY,
  SIDE_OPTIONS,
  directionLabel,
  isNarrowed,
  selectClients,
} from '../lib/clientFilter'

/**
 * Ledger Collector.
 *
 * One job, stated plainly: for each client this month, have we got their
 * statement, and have we pulled our own records?
 *
 * Five columns, each answering a question someone asks out loud: who is this,
 * have they sent it, have we got our side, *have we even chased them*, and when
 * do we ask next. The fourth is the one that settles arguments — "waiting for
 * them" for three weeks means one thing if we messaged twice and rang once, and
 * something else if nobody sent anything.
 *
 * There is no actions column. Four buttons per row was more text than the facts
 * they sat beside, and on forty rows it read as a wall. The row opens a panel
 * instead, and the actions live at the top of it — which is also where someone
 * ends up anyway, because deciding what to do means reading the thread first.
 *
 * Cells hold at most two lines. Anything that needs a sentence — why a file could
 * not be read, why our own side does not add up — is a sentence in the panel or a
 * single banner above the table, not repeated on every row.
 *
 * The language is deliberately ordinary. Anyone in the accounts team should be
 * able to open it having read nothing, and know what to do next without being
 * told what a reconciliation gate is. So: "Not asked yet", not `NOT_REQUESTED`.
 * "Couldn't read the file", not `FAILED_VALIDATION`.
 */

/**
 * Every column carries a `minmax(floor, Nfr)`, not just Client and Chasing —
 * on a wide monitor, extra width used to pour entirely into those two while
 * Balance, Number, Email and the rest sat pinned at their tightest widths,
 * reading as "two columns of empty air next to six that never grew". Now the
 * whole row shares it, in proportion to each column's own weight, so widening
 * the window fills out evenly instead of concentrating in a couple of spots.
 * The floors are unchanged from the 1280-with-a-sidebar sizing this was
 * originally tuned to — nothing gets tighter than it already was.
 */
const COLS =
  'minmax(150px,2fr) minmax(100px,.9fr) minmax(76px,.7fr) minmax(130px,1fr) minmax(120px,.9fr) minmax(140px,1.1fr) minmax(110px,.9fr)'


/**
 * Turn a server or upstream error into something a person can act on.
 *
 * The raw text is accurate and useless to the reader — "Gati GetClientData:
 * Unauthorized" tells an accountant nothing about what to do. Anything not
 * recognised is passed through rather than replaced, so a real message is never
 * swallowed by a friendly generic one.
 */
function humanError(text = '') {
  if (/unauthori[sz]ed|AuthorizationToken/i.test(text)) {
    return "Gati wouldn't let us in — the access token is missing or expired. Someone will need to renew it."
  }
  if (/unreachable|Could not reach|ENOTFOUND|ECONNREFUSED/i.test(text)) {
    return "Couldn't reach Gati just now. It's usually back shortly — try again in a few minutes."
  }
  if (/took too long|timeout/i.test(text)) {
    return 'That took too long. Gati is slow right now — try again.'
  }
  return text
}

/**
 * The most recently closed month, as the two dates the API wants plus a
 * label people say aloud.
 *
 * Not "this month" — a client can't send a ledger for days that haven't
 * happened yet, so a period opened mid-September asks through 31 Aug, not
 * 30 Sep. Roll back one month first; everything below then describes that
 * month, not the calendar's current one.
 */
function currentPeriod() {
  const now = new Date()
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const y = prevMonth.getFullYear()
  const m = prevMonth.getMonth()
  const pad = (n) => String(n).padStart(2, '0')
  const last = new Date(y, m + 1, 0).getDate()
  return {
    from: `${y}-${pad(m + 1)}-01`,
    to: `${y}-${pad(m + 1)}-${pad(last)}`,
    label: `${prevMonth.toLocaleString('en-IN', { month: 'short' })}-${y}`,
  }
}

/** Where the reader's last filter/sort choice is remembered across a reload. */
const FILTER_STORAGE_KEY = 'lereco.ledgerCollector.filters'

/**
 * The saved query, if there is one and it still looks like one.
 *
 * `{ ...DEFAULT_QUERY, ...parsed }` rather than trusting the stored object
 * outright — a field this screen adds later would otherwise come back
 * `undefined` for anyone whose browser saved an older shape, instead of
 * quietly taking the new default.
 */
function loadStoredQuery() {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY)
    if (!raw) return DEFAULT_QUERY
    return { ...DEFAULT_QUERY, ...JSON.parse(raw) }
  } catch {
    return DEFAULT_QUERY
  }
}

export default function LedgerCollector() {
  // Accountants view this screen; only admins act on it.
  const canAct = useCanAct()
  const [period] = useState(currentPeriod)
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState(null)
  /** Latest reconciliation run per request id — keyed by `_id`, for the
   *  "post-reco chase" filter. Loaded alongside `rows`; a request with no
   *  entry here just reads as `collecting` (see `reconciliationPhaseOf`). */
  const [runByRequestId, setRunByRequestId] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [message, setMessage] = useState(null)

  /** Client codes picked for a bulk action, independent of what the filters currently show. */
  const [selected, setSelected] = useState(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  // Enrolling messages real clients on a schedule nobody re-approves each day,
  // and removing them is exactly as deliberate a change to that schedule — so
  // both get a confirm step, symmetric with each other.
  const [confirmingChase, setConfirmingChase] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  // Overlays, one at a time. Each holds the row it was opened from rather
  // than an id, so the panel can render immediately from what the list already
  // knows and fill in detail when its own fetch lands.
  const [activityFor, setActivityFor] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [ledgersFor, setLedgersFor] = useState(null)

  /*
   * The client list now comes from the Track ledger API, not from our own
   * requests — 607 clients with a live balance, where our collection rows only
   * exist for periods somebody has opened. The two are joined by client code:
   * the ledger supplies who and how much, our requests supply what has been
   * chased and received.
   */
  const [ledger, setLedger] = useState({ rows: [], total: 0, complete: true })
  const [ledgerErr, setLedgerErr] = useState(null)
  const [ledgerLoading, setLedgerLoading] = useState(true)

  /** What the reader has narrowed the list to. One object, so Clear is one reset. */
  const [q, setQ] = useState(() => loadStoredQuery())
  const patch = (bits) => setQ((prev) => ({ ...prev, ...bits }))

  // Survives a refresh — read once, on mount, then kept in sync below.
  useEffect(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(q))
    } catch {
      // Private browsing, storage disabled, or a full quota — the filter
      // still works for this visit, it just won't be there after a refresh.
    }
  }, [q])

  /*
   * What is typed in the search box, before it is asked for.
   *
   * Held apart from `q.text` because search runs on submit, not on keystroke —
   * so between typing and pressing Search the box and the list legitimately
   * disagree, and one piece of state cannot represent both. Seeded from the
   * restored `q.text` rather than always starting blank, so a reload with a
   * saved search does not show the box empty while the list is filtered —
   * that mismatch is exactly what `searchPending` below exists to catch.
   */
  const [draft, setDraft] = useState(() => q.text)
  const searchPending = draft.trim() !== q.text

  /**
   * Drop the search, and only the search.
   *
   * Scoped deliberately: this is the cross *inside the search bar*, so it undoes
   * that box and leaves the side, party and status filters alone. Clearing
   * everything from a control that sits in one field would throw away choices
   * made in another.
   */
  const clearSearch = () => {
    setDraft('')
    patch({ text: '' })
  }

  /** Back to the whole list: every filter and the search box together. */
  const clearAll = () => {
    setQ(DEFAULT_QUERY)
    setDraft('')
  }

  /*
   * The whole list, in one call.
   *
   * It used to fetch 25 at a time and pull the next page on scroll. That had to go
   * once this screen grew a search box: the ledger API ignores every search
   * parameter it was offered, so filtering happens here, over whatever rows are in
   * memory — and with a paged list that means searching only the pages somebody
   * had already scrolled past. Typing a client's name would have come back empty
   * purely because they sat on page nine. The full set is 614 rows and 136 KB in
   * 0.17s, which is less work than the scrolling itself would have caused.
   */
  /*
   * Party codes to drop before anything is shown.
   *
   * Three sub-categories, and none of them are clients in the sense this screen
   * means: walk-in retail (counter sales, no running account), `Creditors for
   * Expenses` (electricity, insurance, a credit card, an airline) and `Creditors
   * for Salary` (individual staff salary accounts — our own employees). Together
   * they are 317 of the 539 rows the ledger returns, and chasing any of them for
   * a statement would be a message that should never have been sent.
   *
   * It has to come from our own backend: the ledger API carries a party's
   * `Category` but not its `SubCategory`, so this is the one fact the list needs
   * and cannot get from the source it is built from.
   *
   * `loaded` is tracked separately from the set being empty. Those look identical
   * from an empty array and mean opposite things — "nothing to exclude" versus
   * "we could not find out" — and the second must not quietly pass salary
   * accounts and electricity bills off as clients to chase.
   */
  const [excluded, setExcluded] = useState({ codes: new Set(), loaded: false, error: null })
  /*
   * The one fact Track cannot give: is there a number to chase this client on.
   * Missing entirely (rather than a failed lookup) is not an error worth a
   * banner — it just means every row shows no number until the next reload,
   * which is what the row already does for a client genuinely without one.
   */
  const [contacts, setContacts] = useState(new Map())

  /** Who the daily scheduled chase currently reaches — the census's own record of it. */
  const [autoChaseCodes, setAutoChaseCodes] = useState(new Set())

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true)
    setLedgerErr(null)
    /*
     * Two calls, not three. The census (which codes to drop, and how to reach
     * the ones kept) used to be its own two round trips — merged backend-side
     * into one, since both answers come from the same rows in the same
     * collection. Still independent of the ledger fetch: a census failure must
     * not blank the screen, so it is reported as a banner over a full list
     * rather than as no list at all.
     */
    const [ledgerRes, censusRes] = await Promise.allSettled([fetchAllLedger(), api.listClientScreenData()])

    if (ledgerRes.status === 'fulfilled') setLedger(ledgerRes.value)
    else setLedgerErr(ledgerRes.reason?.message ?? 'Could not load the ledger.')

    if (censusRes.status === 'fulfilled') {
      setExcluded({ codes: new Set(censusRes.value?.clientCodes ?? []), loaded: true, error: null })
      setContacts(new Map(Object.entries(censusRes.value?.contacts ?? {})))
      setAutoChaseCodes(new Set(censusRes.value?.autoChaseCodes ?? []))
    } else {
      setExcluded({
        codes: new Set(),
        loaded: false,
        error: censusRes.reason?.message ?? 'Could not check which parties we run an account with.',
      })
      // Contacts are left as they were rather than cleared: a stale "has a
      // number" is a better failure than every row suddenly reading "no number".
    }

    setLedgerLoading(false)
  }, [])

  useEffect(() => {
    loadLedger()
  }, [loadLedger])

  /*
   * Drop a client from the daily chase the moment this screen sees their
   * balance has settled to zero.
   *
   * This is the one enforcement point there can be: the scheduled pass never
   * calls Track's own ledger API — only a signed-in person's browser can,
   * riding their own session token, since Track has no service credential for
   * it. So a client who pays off between two visits to this screen stays on
   * the cron's list until someone next opens it and this effect runs — not
   * instantly the moment their balance actually clears. Worth knowing if
   * nobody's opened this page in a while.
   */
  useEffect(() => {
    if (!autoChaseCodes.size || !ledger.rows.length) return
    const settled = ledger.rows
      .filter((r) => autoChaseCodes.has(r.clientCode) && r.balancePaise === 0)
      .map((r) => r.clientCode)
    if (!settled.length) return

    let alive = true
    api
      .setAutoChase(settled, false)
      .then(() => {
        if (!alive) return
        setAutoChaseCodes((prev) => {
          const next = new Set(prev)
          settled.forEach((c) => next.delete(c))
          return next
        })
        setMessage({
          tone: 'ok',
          text: `${settled.length} client${settled.length === 1 ? '' : 's'} removed from daily chase — balance settled to zero.`,
        })
      })
      .catch(() => {
        // A background cleanup failing is not worth a banner that could bury
        // whatever the reader was actually doing — it retries on the next load.
      })
    return () => {
      alive = false
    }
  }, [ledger.rows, autoChaseCodes])

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await api.listRequests(period)
      setRows(data.requests)
      setSummary(data.summary)
      // An open drawer must not keep showing pre-action counts.
      setActivityFor((cur) => (cur ? data.requests.find((r) => r._id === cur._id) ?? cur : cur))

      // Best-effort, separate from the requests fetch above — a reconciler
      // hiccup should not take down the whole Collector screen, just leave
      // the "post-reco chase" filter reading everything as "collecting".
      try {
        const { runs } = await api.latestReconciliationRuns(data.requests.map((r) => r._id))
        setRunByRequestId(new Map(runs.map((r) => [r.ledgerRequestId, r])))
      } catch {
        setRunByRequestId(new Map())
      }
    } catch (e) {
      setError(humanError(e.message))
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    load()
  }, [load])

  /** Every action follows the same shape: run it, say what happened, reload. */
  const run = async (id, fn, successText) => {
    setBusyId(id)
    setMessage(null)
    try {
      const result = await fn()
      setMessage({ tone: 'ok', text: typeof successText === 'function' ? successText(result) : successText })
      await load()
    } catch (e) {
      setMessage({ tone: 'bad', text: humanError(e.message) })
    } finally {
      setBusyId(null)
    }
  }

  /*
   * Our requests, keyed by client code.
   *
   * A Map rather than a `find` per row: the list is 614 clients and the filter
   * re-runs on every keystroke, so a linear scan inside the loop would make it
   * 614 × however many periods are open, per character typed.
   */
  const requestByCode = useMemo(() => {
    const m = new Map()
    for (const r of rows) if (r.clientCode) m.set(r.clientCode, r)
    return m
  }, [rows])

  /**
   * The clients this screen is about, before the reader narrows anything.
   *
   * Removed here rather than inside `selectClients`, because this is not a filter
   * — nobody chose it and nobody can switch it off. It changes what the list *is*,
   * so it also becomes the denominator: "17 of 222" should count the clients we
   * collect from, not 618 including the ones we never would.
   */
  /** A client's name for messages, from the Track list — falls back to the code. */
  const nameOfCode = (code) => ledger.rows.find((r) => r.clientCode === code)?.clientName || code

  const collectibleRows = useMemo(
    () => (excluded.codes.size ? ledger.rows.filter((r) => !excluded.codes.has(r.clientCode)) : ledger.rows),
    [ledger.rows, excluded.codes]
  )

  /** Search, filters and sort applied — the rows actually rendered. */
  const visible = useMemo(
    () =>
      selectClients(
        collectibleRows,
        q,
        (code) => requestByCode.get(code) ?? null,
        (code) => autoChaseCodes.has(code),
        (requestId) => runByRequestId.get(requestId) ?? null
      ),
    [collectibleRows, q, requestByCode, autoChaseCodes, runByRequestId]
  )

  const toggleSelected = (code) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  // "Select all" only ever reaches what the filters are currently showing —
  // picking a hundred clients should not silently also pick the four hundred
  // a filter is hiding right now.
  // Only clients the chase can reach are selectable — no WhatsApp number and
  // no usable email means there's nothing to send. One already on the daily
  // chase stays selectable so it can be taken off.
  const selectableCode = useCallback(
    (code) => isReachable(contacts.get(code)) || autoChaseCodes.has(code),
    [contacts, autoChaseCodes]
  )
  const visibleCodes = useMemo(() => visible.map((L) => L.clientCode).filter(selectableCode), [visible, selectableCode])
  const allVisibleSelected = visibleCodes.length > 0 && visibleCodes.every((c) => selected.has(c))
  const someVisibleSelected = !allVisibleSelected && visibleCodes.some((c) => selected.has(c))

  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) visibleCodes.forEach((c) => next.delete(c))
      else visibleCodes.forEach((c) => next.add(c))
      return next
    })
  }

  // How the bulk bar's buttons read: "add" only counts who isn't already on
  // the list, "remove" only who is — pressing either is never a no-op click.
  const selectedList = [...selected]
  const selectedOffList = selectedList.filter((c) => !autoChaseCodes.has(c)).length
  const selectedOnList = selectedList.length - selectedOffList
  // Said before the click commits, not after: enrolling a client with no
  // number on file does nothing until one is added, and that is worth
  // knowing while the confirmation is still open to cancel, not as a banner
  // after the list has already changed.
  const chaseNoNumber = selectedList.filter((c) => !autoChaseCodes.has(c) && !contacts.get(c)?.whatsAppNo).length

  /*
   * Add or drop the selected clients from the daily scheduled chase.
   *
   * This never sends anything itself — it only flips `clients.autoChase`.
   * `POST /api/v1/jobs/chases`, a separate machine-authenticated route meant
   * for an external scheduler (not called from here), is what reads that flag
   * every day and decides, per its own due-date rules, who actually gets a
   * WhatsApp message today.
   *
   * Enrolling also opens (or widens) the current period for exactly the
   * picked codes first. The scheduler only ever looks at existing request
   * rows — most of `selected` has none yet, since the ledger list is built
   * from Track and a row only gets one once a period is opened for it — so
   * without this, flipping the flag on a client with no request document
   * would enrol them in a list the scheduler has nothing to act on. Removing
   * needs none of that: the flag alone is enough to stop it.
   */
  const bulkSetAutoChase = async (enabled) => {
    const codes = [...selected]
    if (!codes.length) return

    setBulkBusy(true)
    setMessage(null)
    try {
      // Nothing is opened for a client nobody can reach — the backend refuses
      // them on the chase too, and says so below.
      const reachable = enabled ? codes.filter((c) => isReachable(contacts.get(c))) : codes
      const opened = enabled && reachable.length ? await api.openPeriod({ ...period, clientCodes: reachable }) : null
      const out = await api.setAutoChase(codes, enabled)

      const emailOnly = enabled ? reachable.filter((c) => !contacts.get(c)?.whatsAppNo).length : 0
      const parts = [
        enabled
          ? `${out.matched} client${out.matched === 1 ? '' : 's'} added to the daily chase list.`
          : `${out.matched} client${out.matched === 1 ? '' : 's'} removed from the daily chase list.`,
      ]
      const held = heldText(opened?.heldForActiveCycle, nameOfCode)
      if (held) parts.push(held)
      if (out.unreachable?.length) {
        parts.push(
          `${out.unreachable.length} not added — no WhatsApp number or email on file: ${out.unreachable.slice(0, 6).map(nameOfCode).join(', ')}${out.unreachable.length > 6 ? ', …' : ''}.`
        )
      }
      if (emailOnly) {
        parts.push(`${emailOnly} ${emailOnly === 1 ? 'has' : 'have'} no WhatsApp number — chased by email only.`)
      }
      setMessage({ tone: 'ok', text: parts.join(' ') })
      setSelected(new Set())
      await Promise.all([load(), loadLedger()])
    } catch (e) {
      setMessage({ tone: 'bad', text: humanError(e.message) })
    } finally {
      setBulkBusy(false)
    }
  }

  if (loading) {
    return (
      <section className="screen">
        <h1 className="page-title">Ledger Collector</h1>
        <SkeletonLine width="320px" size="sm" />
        <div className="stats" style={{ marginTop: 20 }}>
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="stat-accent" />
              <SkeletonLine width="70px" size="sm" />
              <div style={{ marginTop: 8 }}>
                <SkeletonLine width="40px" size="lg" />
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  /*
   * `chaseDueOn` (from `nextChaseDueFor` on the backend), not the raw
   * `nextDueOn` — that field is when this collection *cycle* opened, set once
   * and static until a statement finally arrives, so almost every open row
   * reads as "overdue" under it within a day regardless of whether anything
   * is actually behind. `chaseDueOn` is null whenever nothing is actually due
   * (received, disputed, refused, a promise not yet due, an unclear reply
   * nobody's answered yet) — `daysUntil(null)` is `null`, so those rows never
   * count here, and this card means what it says: a request whose next real
   * chase is already late.
   */
  const overdue = rows.filter((r) => r.status !== 'READY' && daysUntil(r.chaseDueOn) < 0).length
  // A disputed balance is the one thing here that must not wait for someone to
  // scroll to it, so it gets counted in the header like an unread reply does.
  const disputed = rows.filter((r) => r.conversation?.disputed).length
  const incomplete = rows.filter((r) => r.ourSide?.ledgerDocId && !r.ourSide.reconciles).length
  const neverChased = rows.filter((r) => (r.chaseCount ?? 0) === 0 && r.status !== 'READY').length
  const needReading = rows.filter((r) => r.conversation?.state === 'THEY_REPLIED').length
  // The last thing we tried to send this client never went out — a config or
  // template problem on our side, not anything the client did or didn't do.
  // Distinct from every other count here: those are about what a client said
  // or hasn't said, this one means the system itself is silently failing.
  const sendFailingRows = rows.filter((r) => r.conversation?.sendFailed)
  const sendFailing = sendFailingRows.length
  // Named only when every failing row agrees on why — usually true, since one
  // broken template or a config change tends to fail everyone the same way.
  // A mix of causes says less than it costs to read, so it's left unnamed.
  const sendFailingReasons = new Set(sendFailingRows.map((r) => r.conversation?.sendFailedReason).filter(Boolean))
  const sendFailingReason = sendFailingReasons.size === 1 ? [...sendFailingReasons][0] : null
  // The oldest unbroken failure run, in days — the signal that this isn't a
  // blip: a fresh failure and one that has been silently retrying for three
  // weeks read identically as a bare count otherwise.
  const sendFailingDays = Math.max(
    0,
    ...sendFailingRows.map((r) => daysSince(r.conversation?.sendFailedSince)).filter((d) => d != null)
  )

  return (
    <section className="screen lc-screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">Ledger Collector</h1>
          <div className="page-sub">
            Collecting {period.label} ledgers — each client&apos;s statement, chased on WhatsApp and email, and our
            own side from Gati. Open a client to see the whole conversation.
          </div>
        </div>
      </div>

      {error && (
        <div className="gate fail" style={{ marginBottom: 16 }}>
          <span className="gi">!</span>
          <span>{error}</span>
        </div>
      )}

      {message && (
        <div className={`gate ${message.tone === 'ok' ? 'pass' : 'fail'}`} style={{ marginBottom: 16 }}>
          <span className="gi">{message.tone === 'ok' ? '✓' : '!'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {summary && summary.total > 0 && (
        <>
          <p className="page-sub" style={{ marginBottom: 10, fontSize: 13 }}>
            <b style={{ color: 'var(--ink)' }}>
              {summary.READY} of {summary.total}
            </b>{' '}
            clients are fully collected. {summary.REQUESTED > 0 && `${summary.REQUESTED} still owe us a statement. `}
            {/*
              Said first, ahead of anything about what a client has or hasn't
              said — this one means the system itself is silently failing to
              reach them, which is a different kind of urgent from a client
              who simply hasn't answered yet.
            */}
            {sendFailing > 0 && (
              <b style={{ color: 'var(--strain)' }}>
                <button
                  type="button"
                  onClick={() => patch({ problem: 'send_failing' })}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    color: 'inherit',
                    font: 'inherit',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                  }}
                  title="Show only these clients"
                >
                  {sendFailing} {sendFailing === 1 ? "isn't" : "aren't"} getting our messages
                </button>{' '}
                — a sending problem on our side{sendFailingReason ? ` (${sendFailingReason})` : ''}
                {sendFailingDays > 0 ? `, going on ${sendFailingDays} day${sendFailingDays === 1 ? '' : 's'}` : ''}.
                {' '}
              </b>
            )}
            {/*
              Said before anyone escalates: a client who has never been contacted
              is not a client who is ignoring us. This is the sentence that stops
              the wrong conversation happening.
            */}
            {neverChased > 0 && (
              <b style={{ color: 'var(--red)' }}>
                {neverChased} {neverChased === 1 ? 'has' : 'have'} never been contacted.{' '}
              </b>
            )}
            {/* A reply nobody has read is the one thing here that goes stale. */}
            {needReading > 0 && (
              <b style={{ color: 'var(--good)' }}>
                {needReading} {needReading === 1 ? 'has' : 'have'} replied and not been read.{' '}
              </b>
            )}
            {disputed > 0 && (
              <b style={{ color: 'var(--alert)' }}>
                {disputed} {disputed === 1 ? 'has' : 'have'} disputed a balance — that needs a person.{' '}
              </b>
            )}
          </p>

          <div className="stats">
            {[
              ['Not asked yet', summary.NOT_REQUESTED, 'Opened this period, first message not sent'],
              ['Waiting on them', summary.REQUESTED, 'Asked — their statement not in yet'],
              ['Statement received', summary.RECEIVED + summary.READY, `${summary.READY} with our side pulled too`],
              ['Chase overdue', overdue, 'Next reminder is past its due date'],
            ].map(([label, n, meta]) => (
              <div className="stat" key={label}>
                <div className="stat-accent" />
                <div className="stat-label">{label}</div>
                <div className="stat-value">{n}</div>
                <div className="stat-meta">{meta}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/*
        Said once, not on every row. Our side is Gati's full account ledger now,
        so its rows should always add up to Gati's own running balance — when
        they don't, Gati sent something inconsistent and a person should look
        before that client is reconciled.
      */}
      {incomplete > 0 && (
        <div className="gate warn" style={{ marginTop: 16 }}>
          <span className="gi">!</span>
          <span>
            <b>
              {incomplete} {incomplete === 1 ? 'client' : 'clients'}
            </b>{' '}
            where our ledger from Gati doesn’t add up to Gati’s own closing balance. Pull our side again, and check
            the account in Gati before reconciling.
          </span>
        </div>
      )}

      {/*
        Gated on the ledger, not on our own requests. The client list comes from
        Track now — 607 of them, whether or not a period has been opened here — so
        an empty screen means the ledger returned nothing, not that nobody has
        pressed Start. Opening a period adds collection state to rows that already
        exist rather than creating the list.
      */}
      {/*
        Search, filters, sort, and the count.

        Only shown once there is a list to narrow — a search box above an empty
        screen invites someone to type into it and conclude the data is missing.

        Labels sit above their controls and the whole bar stays on one line, the
        same arrangement as the folder view, so the two screens do not each teach
        a different layout. Sort is one button showing the active order because
        there are only two orders: the current state is the whole message.
      */}
      {collectibleRows.length > 0 && (
        <div className="lfilter" style={{ marginTop: 18 }}>
          {/*
            Search runs when it is asked to, not on every keystroke.

            So the typed text and the applied text are two different things:
            `draft` is what is in the box, `q.text` is what the list is actually
            filtered by. They are deliberately allowed to disagree — that gap is
            what makes the button meaningful, and the button lights up while it
            exists so the state is visible rather than something to remember.

            A real <form>, so Enter submits for free and the button is a plain
            submit — the two ways of asking cannot drift apart, and neither needs
            its own handler.
          */}
          <form
            className="lfield"
            style={{ flex: '0 1 280px' }}
            onSubmit={(e) => {
              e.preventDefault()
              patch({ text: draft.trim() })
            }}
          >
            <span className="lfilter-lbl">Search</span>
            <div className="lfield-row">
              {/*
                The control lives inside the field, at its right edge, and is one
                button with two jobs — never both at once, the same rule the date
                filter follows.

                  something typed but not run  →  magnifier, runs it
                  a search already applied     →  cross, drops it
                  nothing typed, nothing run   →  no button at all

                One control rather than two because the two states are mutually
                exclusive by construction: `searchPending` is exactly "the box
                disagrees with the list", so a cross can only appear once they
                agree, and there is never a moment where both would be true.
              */}
              <div className="csearch-wrap">
                <input
                  type="text"
                  className="csearch"
                  placeholder="Client name or code"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  // Escape abandons the whole search — box and applied filter
                  // both — which is the fastest way back to the full list and
                  // matches what Escape does everywhere else in the app.
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') clearSearch()
                  }}
                />
                {searchPending ? (
                  <button type="submit" className="csearch-act" title="Search" aria-label="Search">
                    <Icon name="search" size={14} />
                  </button>
                ) : q.text ? (
                  <button
                    type="button"
                    className="csearch-act"
                    onClick={clearSearch}
                    title="Clear this search"
                    aria-label="Clear this search"
                  >
                    <Icon name="x" size={14} />
                  </button>
                ) : null}
              </div>
            </div>
          </form>

          <div className="lfield" style={{ flex: '1 1 560px' }}>
            {/* No umbrella "Filter" label here any more — each control now
                carries its own caption above it, which already says what it
                is without a second, redundant label above the whole row. */}
            <div className="lfield-row">
              <FilterField label="Balance">
                {/* Three short labels, all visible. A cycling button would hide two
                    of them and make "which sides are there" a thing to discover by
                    clicking. */}
                <Segmented
                  options={SIDE_OPTIONS}
                  value={q.side}
                  onChange={(side) => patch({ side })}
                  title="Show only debit or only credit balances"
                />
              </FilterField>
              <FilterField label="Party">
                <Select
                  options={CATEGORY_OPTIONS}
                  value={q.category}
                  onChange={(category) => patch({ category })}
                  title="Customers or suppliers"
                />
              </FilterField>
              <FilterField label="Collection">
                <Select
                  options={COLLECTION_OPTIONS}
                  value={q.collection}
                  onChange={(collection) => patch({ collection })}
                  title="How far along this period's collection is"
                />
              </FilterField>
              <FilterField label="Daily chase">
                <Select
                  options={CHASE_OPTIONS}
                  value={q.chase}
                  onChange={(chase) => patch({ chase })}
                  title="Only clients on, or off, the daily scheduled chase list"
                />
              </FilterField>
              <FilterField label="Problems">
                <Select
                  options={PROBLEM_OPTIONS}
                  value={q.problem}
                  onChange={(problem) => patch({ problem })}
                  title="Clients our own messages are failing to reach — a config or template problem, not the client"
                />
              </FilterField>
              <FilterField label="Reconciliation">
                <Select
                  options={RECONCILIATION_OPTIONS}
                  value={q.reconciliation}
                  onChange={(reconciliation) => patch({ reconciliation })}
                  title="Still collecting their statement, or already reconciled and — if it found issues on their side — being chased for that instead"
                />
              </FilterField>
              {/* Offered only while something is actually narrowed — a Clear that
                  cannot clear anything is a dead control. */}
              {isNarrowed(q) && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={clearAll} style={{ alignSelf: 'flex-end' }}>
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="lfield">
            <span className="lfilter-lbl">Sort</span>
            {/*
              Two toggles, not one. Field and direction are independent choices —
              "by balance" and "biggest first" are different questions — and a
              single control cycling all four combinations would make reaching
              one of them a guessing game about how many clicks are left.
            */}
            <div className="lfield-row">
              <button
                type="button"
                className="btn btn-sm sortswap"
                onClick={() => patch({ sortBy: q.sortBy === 'name' ? 'balance' : 'name' })}
                title="Click to sort by the other column"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                  <path d="M7 4v16m0 0l-3.5-3.5M7 20l3.5-3.5M17 20V4m0 0l-3.5 3.5M17 4l3.5 3.5" />
                </svg>
                Sort: {q.sortBy === 'name' ? 'Name' : 'Balance'}
              </button>
              {/*
                The arrow points the way the list actually runs, and the label
                says what that means for this column — "High first" rather than
                "descending", because the reader is picking an outcome, not
                naming an algorithm.
              */}
              <button
                type="button"
                className="btn btn-sm sortdir"
                onClick={() => patch({ sortDir: q.sortDir === 'asc' ? 'desc' : 'asc' })}
                title="Click to reverse the order"
                aria-label={`Sorted ${q.sortDir === 'asc' ? 'ascending' : 'descending'} — click to reverse`}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  aria-hidden="true"
                  className={q.sortDir === 'desc' ? 'flip' : undefined}
                >
                  <path d="M12 20V4m0 0l-5 5m5-5l5 5" />
                </svg>
                {directionLabel(q)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        The count, under the rule and above the table.

        Two numbers rather than one whenever a filter is on: "37 of 539 clients"
        answers both "how many am I looking at" and "how many are there", and the
        second is the one that tells someone their filter is hiding most of the
        list. Both are counted after the excluded categories are dropped, so the
        denominator is the number of clients this screen is actually about.

        The two ways the list can be less than the whole truth each say so, and
        they are different failures: a page of the ledger never arrived, or we
        could not find out which parties we run an account with.
      */}
      {collectibleRows.length > 0 && (
        <div className="lcount">
          {visible.length === collectibleRows.length ? (
            <>
              <b>{collectibleRows.length}</b> clients
            </>
          ) : (
            <>
              <b>{visible.length}</b> of {collectibleRows.length} clients
            </>
          )}
          {!ledger.complete && (
            <span className="lcount-warn">
              {' '}
              · only {ledger.rows.length} of {ledger.total} loaded, so this list is incomplete
            </span>
          )}
          {excluded.error && (
            <span className="lcount-warn"> · non-trade parties could not be excluded</span>
          )}
        </div>
      )}

      {/*
        Two buttons, not one toggle — a mixed selection (some already on the
        list, some not) has a sensible action either way, and a single button
        would have to guess which one somebody meant. Each button's own count
        is only who it would actually change, so neither ever reads as a
        no-op click.
      */}
      {canAct && selected.size > 0 && (
        <div className="lbulkbar">
          <span className="lbulkbar-n">
            <b>{selected.size}</b> selected
          </span>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
          {selectedOnList > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={bulkBusy}
              onClick={() => setConfirmingRemove(true)}
              title="Stop the daily scheduled chase for these clients"
            >
              Remove from daily chase
            </button>
          )}
          {selectedOffList > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={bulkBusy}
              onClick={() => setConfirmingChase(true)}
              title="The daily scheduled chase will reach these clients from its next run"
            >
              Add to daily chase
            </button>
          )}
        </div>
      )}

      {/*
        Said plainly rather than left as a silently wider list. Without the
        exclusion the screen shows ~79 walk-in retail parties as clients to chase
        — each one a statement request nobody should send — so the reader needs to
        know the list is not the one they think they are reading.
      */}
      {excluded.error && (
        <div className="gate warn" style={{ marginBottom: 12 }}>
          <span className="gi">!</span>
          <span>
            Couldn&apos;t check which parties are walk-in retail, so they are still listed. Direct Customers have no
            running account and nothing to collect — don&apos;t chase them. {excluded.error}
          </span>
        </div>
      )}

      {ledgerLoading && collectibleRows.length === 0 ? (
        <div className="tbl tbl-collector" style={{ marginTop: 4 }}>
          <div className="thead-row">
            {/* An unchecked spacer, not a checkbox — nothing is selectable until
                there is a list to select from, but the header still has to line
                up with the rows the skeleton beneath it is shaped like. */}
            {canAct && <div className="rwsel theadsel" />}
            <div className="thead" style={{ gridTemplateColumns: COLS }}>
              <div>Client</div>
              <div>Balance</div>
              <div>Their statement</div>
              <div>Chase status</div>
              <div>WhatsApp</div>
              <div>Email</div>
              <div>Next chase</div>
            </div>
          </div>
          <div className="lc-rows">
            <SkeletonRow cols={COLS} cells={7} count={10} />
          </div>
        </div>
      ) : collectibleRows.length === 0 ? (
        <div className="empty">
          {/*
            The only place this action appears.
            It creates one row per client for the period, seeded from Gati's client
            master — so it is worth pressing exactly once a period, and a top-bar
            button that does nothing useful on the other thirty days is clutter.
            Here it sits next to the one sentence that explains it.
          */}
          <p style={{ fontSize: 14, color: 'var(--ink)', marginBottom: 8 }}>
            Nothing being collected for {period.label} yet.
          </p>
          <p style={{ marginBottom: 14 }}>
            This creates one line per client, so you can track who has sent their statement and who hasn&apos;t.
          </p>
          {canAct && (
          <button
            className="btn btn-primary"
            disabled={busyId === 'period'}
            onClick={() =>
              run('period', () => api.openPeriod(period), (out) =>
                [`Ready to collect ${period.label} statements.`, heldText(out?.heldForActiveCycle, nameOfCode)].filter(Boolean).join(' ')
              )
            }
          >
            {busyId === 'period' ? 'Starting…' : `Start ${period.label}`}
          </button>
          )}
        </div>
      ) : visible.length === 0 ? (
        /*
          Clients exist, the filter matched none of them. Said as a filter result
          rather than an empty list, with the way out attached — otherwise a narrow
          filter reads as the ledger having gone away.
        */
        <div className="empty">
          <p style={{ fontSize: 14, color: 'var(--ink)', marginBottom: 8 }}>
            No client matches this search.
          </p>
          <p style={{ marginBottom: 14 }}>
            All {collectibleRows.length} are still there — the filters above are hiding them.
          </p>
          <button className="btn btn-sm" onClick={clearAll}>
            Clear filters
          </button>
        </div>
      ) : (
        <div className="tbl tbl-collector" style={{ marginTop: 4 }}>
          <div className="thead-row">
            {canAct ? (
            <label className="rwsel" title="Select all visible clients">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someVisibleSelected
                }}
                onChange={toggleSelectAllVisible}
                aria-label="Select all visible clients"
              />
            </label>
            ) : null}
            <div className="thead" style={{ gridTemplateColumns: COLS }}>
              <div>Client</div>
              <div>Balance</div>
              <div>Their statement</div>
              <div>Chase status</div>
              <div>WhatsApp</div>
              <div>Email</div>
              <div>Next chase</div>
            </div>
          </div>

          {/*
            The one scrolling region on this screen — everything above it
            (title, search/filter/sort, the count, this header row) has a
            fixed place and stays there; only the client list moves.
          */}
          <div className="lc-rows">
          {visible.map((L) => {
            /*
             * The ledger supplies the client and the balance; our own request —
             * when a period has been opened — supplies what has been chased and
             * received. Most of the 607 will not have one, so a placeholder
             * stands in and the collection columns simply read as untouched.
             */
            const r = rows.find((x) => x.clientCode === L.clientCode) ?? {
              _id: `ledger-${L.clientCode}`,
              clientCode: L.clientCode,
              clientName: L.clientName,
              chaseCounts: {},
              chaseCount: 0,
              conversation: { state: 'NOT_STARTED', unread: 0 },
              theirSide: {},
              ourSide: {},
            }
            const chased = r.chaseCount ?? 0

            return (
              <div className="rw" key={r._id}>
                {/*
                  Two targets now: the row opens the client's collected ledgers
                  (folders, one per ask), and the WhatsApp chip opens the chat
                  thread. The ledgers are what someone opens to reconcile; the
                  thread is for chasing — different jobs, different clicks.
                */}
                {/*
                  A div rather than a <button>, because the row now contains
                  buttons and nesting them is invalid — and a browser will drop the
                  inner one. Role and key handling are given explicitly so it stays
                  reachable from the keyboard.
                */}
                <CollectionRow
                  request={r}
                  clientName={L.clientName}
                  balance={L}
                  whatsAppNo={contacts.get(r.clientCode)?.whatsAppNo ?? null}
                  email={contacts.get(r.clientCode)?.email ?? null}
                  cols={COLS}
                  onOpen={() => setLedgersFor(r)}
                  onWhatsApp={() => setActivityFor(r)}
                  onEmail={() => setActivityFor(r)}
                  onView={({ side }) => setViewing({ request: r, side })}
                  selected={selected.has(L.clientCode)}
                  selectable={selectableCode(L.clientCode)}
                  onToggleSelect={canAct ? () => toggleSelected(L.clientCode) : undefined}
                  autoChasing={autoChaseCodes.has(L.clientCode)}
                />
              </div>
            )
          })}
          </div>
        </div>
      )}

      {ledgerErr && (
        <div className="note" style={{ borderColor: 'var(--alert)', marginTop: 12 }}>
          {ledgerErr}
        </div>
      )}

      {activityFor && (
        <ActivityDrawer request={activityFor} onClose={() => setActivityFor(null)} />
      )}
      {ledgersFor && <ClientLedgers request={ledgersFor} onClose={() => setLedgersFor(null)} onChanged={load} />}

      {viewing && (
        <LedgerView request={viewing.request} side={viewing.side} onClose={() => setViewing(null)} />
      )}

      {confirmingChase && (
        <Modal
          title="Add to daily chase"
          dismissible={!bulkBusy}
          onClose={() => setConfirmingChase(false)}
          footer={
            <>
              <button className="btn" disabled={bulkBusy} onClick={() => setConfirmingChase(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={bulkBusy}
                onClick={async () => {
                  await bulkSetAutoChase(true)
                  setConfirmingChase(false)
                }}
              >
                {bulkBusy ? 'Adding…' : 'Add'}
              </button>
            </>
          }
        >
          <p>
            Add <b>{selectedOffList}</b> client{selectedOffList === 1 ? '' : 's'} to daily chase?
          </p>
          {chaseNoNumber > 0 && (
            <p className="mute" style={{ marginTop: 8 }}>
              <b>{chaseNoNumber}</b> {chaseNoNumber === 1 ? 'has' : 'have'} no WhatsApp number on file —{' '}
              {chaseNoNumber === 1 ? 'it' : 'they'}&apos;ll be chased by email only.
            </p>
          )}
        </Modal>
      )}

      {confirmingRemove && (
        <Modal
          title="Remove from daily chase"
          dismissible={!bulkBusy}
          onClose={() => setConfirmingRemove(false)}
          footer={
            <>
              <button className="btn" disabled={bulkBusy} onClick={() => setConfirmingRemove(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={bulkBusy}
                onClick={async () => {
                  await bulkSetAutoChase(false)
                  setConfirmingRemove(false)
                }}
              >
                {bulkBusy ? 'Removing…' : 'Remove'}
              </button>
            </>
          }
        >
          <p>
            Remove <b>{selectedOnList}</b> client{selectedOnList === 1 ? '' : 's'} from the daily chase
            list? The scheduled chase will stop reaching {selectedOnList === 1 ? 'it' : 'them'} from its
            next run.
          </p>
        </Modal>
      )}
    </section>
  )
}


/**
 * One filter control, with its own small caption above it.
 *
 * The Filter row holds five of these side by side — without its own label,
 * a lone "Chasing" or the second "All" answers a question nobody can see
 * being asked. The umbrella "Filter" label above the whole row says there
 * are filters here at all; this says which one each control is.
 */
function FilterField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span className="lfilter-lbl" style={{ fontSize: 9 }}>{label}</span>
      {children}
    </div>
  )
}

/**
 * A few short options, all visible, one selected.
 *
 * Used where a cycling button would hide the choices: "Dr" only reads as a filter
 * once you can see "Cr" beside it. Kept to labels of a few characters — the moment
 * the options need words, a select is narrower and does not push the row of
 * controls onto a second line.
 *
 * `radiogroup` rather than a row of buttons, because that is what it is: one
 * choice out of a visible set, and a screen reader should say so.
 */
function Segmented({ options, value, onChange, title }) {
  return (
    <div className="seg" role="radiogroup" title={title}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`seg-b${o.value === value ? ' on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

